/*
 *  -----------------------------------------------------------------------
 *    <copyright file="validation.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import i18n from 'i18next';
import moment from 'moment';
import Constants from '@utils/constants';

const minYear = 1971;

interface CheckRequiredFieldsInterface {
    fields: any;
    state: any;
    setErrors: any;
    errors: any;
}

// validate text fields for showing errors or number of remaining characters
// returns object with field name and error.
export const validateFields = (value?: string, field?: string, length?: number | null, required = false) => {
    const newValue = value ? `${value}`.trim() : value;
    if (field?.length && length && newValue?.length) {
        const valueLength = value?.length ?? 0;
        if (valueLength > length) {
            const remaining = length - valueLength;

            return remaining > 0
                ? { [field]: `${remaining} ${i18n.t(remaining === 1 ? 'characterRemaining' : 'charactersRemaining')}` }
                : {
                      [field]: `${i18n.t('limitExceededBy')} ${Math.abs(remaining)} ${i18n.t(
                          remaining === -1 ? 'character' : 'characters',
                      )}`,
                  };
        }
        return {
            [field]: null,
        };
    }
    if (!newValue?.length && required)
        return {
            [field as string]: `${i18n.t('ra:requiredFieldError')}`,
        };
    return {
        [field as string]: null,
    };
};

export const validateFieldLength = (value: string, field: string, length?: number) =>
    validateFields(value, field, length)[field];

export const validateLimitExceeded = (value: string, length: number) => {
    if (value?.length > length) {
        return `${i18n.t('limitExceeded')}`;
    }
};

// check fields if it's empty and setErrors to component
// returns boolean which indicates if all fields is valid, so have values
export const checkRequiredFields = ({ fields, state, setErrors, errors }: CheckRequiredFieldsInterface) => {
    let isValid = true;
    let newErrors = { ...errors };

    // go through all fields
    fields.forEach((field: any) => {
        let value = state[field];
        if (typeof value === 'number') value = value.toString();
        if (Array.isArray(value)) value = value.join('');

        // check if empty
        const error = validateFields(value, field, 0, true);
        newErrors = {
            ...newErrors,
            ...error,
        };
    });
    if (Object.keys(newErrors).filter((item) => newErrors[item]).length > 0) isValid = false;
    setErrors({ ...newErrors });
    return isValid;
};

export const validateDate = (value: string | Date) =>
    value ? moment(value).year().toString().length === 4 || moment(value).year() > minYear : true;

export const checkIfNameExist = (element: any, itemsArray: any, fieldToCheck: string | string[], idField?: string) => {
    if (typeof fieldToCheck === 'string')
        return !!itemsArray?.find(
            (item: any) =>
                item[fieldToCheck] &&
                `${item[fieldToCheck]}`.toLowerCase() === `${element[fieldToCheck]}`.toLowerCase() &&
                item[idField ?? 'id'] !== element[idField ?? 'id'],
        );
    if (typeof fieldToCheck === 'object') {
        let result = false;
        itemsArray?.forEach((item: any) => {
            let amount = 0;
            fieldToCheck.forEach((el: any) => {
                if (
                    item[el]?.toString().toLowerCase() === element[el]?.toString().toLowerCase() &&
                    item[idField ?? 'id'] !== element[idField ?? 'id']
                ) {
                    amount += 1;
                }
                if (amount === 2) {
                    result = true;
                }
            });
            if (result) return;
        });
        return result;
    }
};

export const isValidObjectId = (id: string) => id !== Constants.ObjectId.Empty && id.match(/^[0-9a-fA-F]{24}$/);

export const checkConfigIdErrors = (item: any, list: any[], value?: string) => {
    const formData = {
        ...item,
        ...(value ? { configId: value } : {}),
    };
    const ifConfigIdExists = checkIfNameExist(formData, list, 'configId');

    if (ifConfigIdExists && formData.configId !== Constants.ObjectId.Empty) {
        return i18n.t('admin:uniqueConfigIdError');
    }

    if (
        !(formData.configId && (formData.configId === Constants.ObjectId.Empty || isValidObjectId(formData.configId)))
    ) {
        return i18n.t('admin:validConfigIdError');
    }

    return null;
};

export const checkFrequencyFields = (name: string, value: string, state: any) => {
    if (
        name === 'reviewFrequencyUnits' &&
        ((value === 'NONE' && state.reviewFrequencyNumber) || (value !== 'NONE' && !state.reviewFrequencyNumber))
    )
        return {
            reviewFrequencyUnits: i18n.t('ra:theseFieldsBothBeSelected'),
            reviewFrequencyNumber: i18n.t('ra:theseFieldsBothBeSelected'),
        };
    if (
        name === 'reviewFrequencyNumber' &&
        ((!value && state.reviewFrequencyUnits !== 'NONE') || (value && state.reviewFrequencyUnits === 'NONE'))
    )
        return {
            reviewFrequencyUnits: i18n.t('ra:theseFieldsBothBeSelected'),
            reviewFrequencyNumber: i18n.t('ra:theseFieldsBothBeSelected'),
        };
    return { reviewFrequencyUnits: '', reviewFrequencyNumber: '' };
};

export const isNumberKey = (evt: any) => /^\d+$/.test(evt.target.value);

// https://www.codegrepper.com/code-examples/javascript/javascript+check+if+json+object+is+valid
export const isJson = (item: any) => {
    item = typeof item !== 'string' ? JSON.stringify(item) : item;

    try {
        item = JSON.parse(item);
    } catch (e) {
        return false;
    }

    if (typeof item === 'object' && item !== null) return true;

    return false;
};

export const isStringNumeric = (str: any) => !isNaN(str) && !isNaN(parseFloat(str));

export const isSplittedValue = (str: any) => typeof str === 'string' && str.includes('|');

export const validOverviewDate = (input: string) => {
    const cuurentInputValue = isSplittedValue(input) ? input.split('|')[0] : input;

    return (
        moment(cuurentInputValue, ['DD-MMM-YYYY', 'MMM-YYYY', moment.ISO_8601], true)?.isValid() &&
        validateDate(cuurentInputValue)
    );
};
